import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Plus } from 'lucide-react';
import LazyPaneRenderer from './LazyPaneRenderer';
import ResizablePane from './ResizablePane';

/**
 * Full grid builder for multi-row layouts.
 *
 * Each pane is draggable; dropping ON another pane moves it next to that
 * pane (within or across rows). A thin "+ Insert row here" strip between
 * rows doubles as a drop target — drop a pane there to spawn a new row.
 *
 * Row heights default to equal flex-1. A draggable handle between rows
 * adjusts each row's `flex-grow` (stored in `rowHeights` keyed by row
 * index — purely visual, NOT persisted server-side yet to keep schema
 * stable; flush to `rowHeights` localStorage so it survives reloads).
 *
 * Empty rows (created by dragging out the last pane) are pruned on save.
 */

const moveToCell = (grid, src, dst) => {
  const copy = grid.map((row) => [...row]);
  const [pane] = copy[src.rowIdx].splice(src.colIdx, 1);
  let { rowIdx, colIdx } = dst;
  if (rowIdx === src.rowIdx && colIdx > src.colIdx) colIdx -= 1;
  copy[rowIdx].splice(colIdx, 0, pane);
  return copy.filter((row) => row.length > 0);
};

const moveToNewRow = (grid, src, insertAt) => {
  const copy = grid.map((row) => [...row]);
  const [pane] = copy[src.rowIdx].splice(src.colIdx, 1);
  // After the splice, the row above the insertion point may shift down.
  let target = insertAt;
  if (insertAt > src.rowIdx && copy[src.rowIdx].length === 0) target -= 1;
  copy.splice(target, 0, [pane]);
  return copy.filter((row) => row.length > 0);
};

const ROW_HEIGHTS_KEY = 'dashboardRowHeights';

const RowResizer = ({ onResize, isHovered, onHover }) => (
  <div
    role="separator"
    aria-orientation="horizontal"
    onPointerDown={onResize}
    onMouseEnter={() => onHover(true)}
    onMouseLeave={() => onHover(false)}
    data-testid="grid-row-resizer"
    style={{ touchAction: 'none' }}
    className={`group relative z-20 cursor-row-resize h-4 -my-2 rounded-full transition-colors ${
      isHovered ? 'bg-purple-500' : 'bg-transparent hover:bg-purple-300'
    }`}
  />
);

const InsertRowDropZone = ({ active, dragging, onDragOver, onDrop, index }) => (
  <div
    onDragOver={onDragOver}
    onDrop={onDrop}
    aria-label={`Insert row at position ${index}`}
    data-testid={`grid-insert-row-${index}`}
    // Only consume pointer events when a pane is actively being dragged;
    // otherwise the row-resize handle (which sits at the same seam) wins.
    style={dragging ? undefined : { pointerEvents: 'none' }}
    className={`relative z-10 transition-all ${
      active
        ? 'h-12 my-1 bg-purple-500/30 border-2 border-dashed border-purple-500 rounded-md flex items-center justify-center'
        : dragging
          ? 'h-3 -my-0.5 hover:h-6 hover:bg-purple-500/10 rounded-md flex items-center justify-center'
          : 'h-0 my-0'
    }`}
  >
    {active && (
      <span className="text-xs font-semibold uppercase tracking-widest text-purple-700 dark:text-purple-300">
        Drop here to create a new row
      </span>
    )}
    {dragging && !active && (
      <span className="hidden group-hover:flex items-center gap-1 text-[10px] uppercase tracking-widest text-purple-600">
        <Plus className="w-3 h-3" />
        New row
      </span>
    )}
  </div>
);

const DashboardGridBuilder = ({
  layoutGrid,
  availablePanes,
  paneWidths,
  selectedVerse,
  aiInsights,
  showAIInsights,
  isLoadingAI,
  onPaneResize,
  onGridChange,
  onRemovePane,
  onVerseChange,
  onAIAnalyze,
}) => {
  const [dragSource, setDragSource] = useState(null);  // { paneId, rowIdx, colIdx }
  const [hoverCell, setHoverCell] = useState(null);    // { rowIdx, colIdx } during drag
  const [dragOverNewRowAt, setDragOverNewRowAt] = useState(null);
  const [rowHeights, setRowHeights] = useState(() => {
    try {
      const raw = window.localStorage.getItem(ROW_HEIGHTS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [hoveredResizer, setHoveredResizer] = useState(null);
  const resizeStateRef = useRef(null);

  const handleDragStart = (e, paneId, rowIdx, colIdx) => {
    setDragSource({ paneId, rowIdx, colIdx });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', paneId); } catch { /* ignore */ }
  };

  const handleDragEnd = () => {
    setDragSource(null);
    setHoverCell(null);
    setDragOverNewRowAt(null);
  };

  const handleCellDragOver = (e, rowIdx, colIdx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverCell?.rowIdx !== rowIdx || hoverCell?.colIdx !== colIdx) {
      setHoverCell({ rowIdx, colIdx });
    }
  };

  const handleCellDrop = (e, rowIdx, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragSource) return;
    if (dragSource.rowIdx === rowIdx && dragSource.colIdx === colIdx) {
      handleDragEnd();
      return;
    }
    const next = moveToCell(layoutGrid, dragSource, { rowIdx, colIdx });
    onGridChange(next);
    handleDragEnd();
  };

  const handleNewRowDragOver = (e, insertAt) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverNewRowAt !== insertAt) setDragOverNewRowAt(insertAt);
  };

  const handleNewRowDrop = (e, insertAt) => {
    e.preventDefault();
    if (!dragSource) return;
    // Can't make a new row if this is the last pane in its row and that row
    // is the only row — that's just a no-op.
    if (layoutGrid.length === 1 && layoutGrid[0].length === 1) {
      handleDragEnd();
      return;
    }
    const next = moveToNewRow(layoutGrid, dragSource, insertAt);
    onGridChange(next);
    handleDragEnd();
  };

  const startRowResize = (e, rowIdx) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeights = { ...rowHeights };
    // Pixels per flex unit, measured from the row above so the seam tracks the finger/cursor.
    const rowEl = e.currentTarget.previousElementSibling;
    const pxPerUnit = Math.max(50, (rowEl?.offsetHeight || 200) / (startHeights[rowIdx] ?? 1));
    // Snapshot every row's current flex-grow weight so we can redistribute
    // the change across ALL rows below the dragged separator, not just the
    // adjacent one. This lets users resize between non-adjacent rows.
    const rowCount = layoutGrid.length;
    const heightAt = (i) => startHeights[i] ?? 1;
    const aboveStart = heightAt(rowIdx);
    const belowRows = Array.from({ length: rowCount - rowIdx - 1 }, (_, i) => rowIdx + 1 + i);
    const belowStartTotal = belowRows.reduce((acc, i) => acc + heightAt(i), 0);
    resizeStateRef.current = { rowIdx, startY, aboveStart, belowRows, belowStartTotal };

    const move = (ev) => {
      const dy = ev.clientY - resizeStateRef.current.startY;
      const delta = dy / pxPerUnit;
      const newAbove = Math.max(0.3, resizeStateRef.current.aboveStart + delta);
      // Spread the inverse delta across all rows below, weighted by their
      // current proportion of the total. Cap each at 0.3 minimum.
      const newBelowTotal = Math.max(0.3 * resizeStateRef.current.belowRows.length,
        resizeStateRef.current.belowStartTotal - delta);
      const factor = resizeStateRef.current.belowStartTotal > 0
        ? newBelowTotal / resizeStateRef.current.belowStartTotal
        : 1;
      setRowHeights((prev) => {
        const next = { ...prev, [resizeStateRef.current.rowIdx]: newAbove };
        for (const i of resizeStateRef.current.belowRows) {
          next[i] = Math.max(0.3, heightAt(i) * factor);
        }
        try { window.localStorage.setItem(ROW_HEIGHTS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      resizeStateRef.current = null;
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  };

  const resetRowHeights = () => {
    setRowHeights({});
    try { window.localStorage.removeItem(ROW_HEIGHTS_KEY); } catch { /* ignore */ }
  };

  const widthFor = (paneId, rowLen) => {
    if (rowLen === 1) return 100;
    if (paneWidths[paneId]) return paneWidths[paneId];
    return rowLen === 2 ? 50 : 100 / rowLen;
  };

  return (
    <div
      className="h-full flex flex-col gap-1 p-4 pb-4 overflow-hidden"
      data-testid="dashboard-grid-shell"
    >
      {Object.keys(rowHeights).length > 0 && (
        <div className="flex justify-end -mb-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetRowHeights}
            className="h-6 text-[10px] text-slate-500 hover:text-slate-700 px-2"
            data-testid="grid-reset-row-heights"
          >
            Reset row heights
          </Button>
        </div>
      )}
      <InsertRowDropZone
        index={0}
        dragging={!!dragSource}
        active={dragOverNewRowAt === 0 && !!dragSource}
        onDragOver={(e) => handleNewRowDragOver(e, 0)}
        onDrop={(e) => handleNewRowDrop(e, 0)}
      />

      {layoutGrid.map((row, rowIdx) => (
        <React.Fragment key={`row-${rowIdx}`}>
          <div
            className="min-h-0 flex gap-0 overflow-hidden"
            style={{ flex: `${rowHeights[rowIdx] ?? 1} 1 0%` }}
            data-testid={`grid-row-${rowIdx}`}
          >
            {row.map((paneId, colIdx) => {
              const label = availablePanes.find((p) => p.id === paneId)?.label || paneId;
              const isLast = colIdx === row.length - 1;
              const isHover = hoverCell?.rowIdx === rowIdx && hoverCell?.colIdx === colIdx;
              const isSource = dragSource?.rowIdx === rowIdx && dragSource?.colIdx === colIdx;
              return (
                <ResizablePane
                  key={`${paneId}-${rowIdx}-${colIdx}`}
                  paneId={paneId}
                  initialWidth={widthFor(paneId, row.length)}
                  minWidth={15}
                  maxWidth={85}
                  onResize={onPaneResize}
                  isResizable={!isLast && row.length > 1}
                  className="h-full"
                >
                  <div
                    onDragOver={(e) => handleCellDragOver(e, rowIdx, colIdx)}
                    onDrop={(e) => handleCellDrop(e, rowIdx, colIdx)}
                    className={`h-full relative rounded-lg transition-all ${
                      isSource ? 'opacity-40' : ''
                    } ${
                      isHover && !isSource && dragSource ? 'ring-2 ring-purple-500 ring-offset-2' : ''
                    }`}
                    data-testid={`grid-cell-${rowIdx}-${colIdx}`}
                  >
                    <div className="h-full bg-card rounded-lg shadow-sm overflow-hidden flex flex-col">
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, paneId, rowIdx, colIdx)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center justify-between gap-2 px-3 py-1.5 border-b cursor-move bg-slate-50 dark:bg-slate-900/50"
                      >
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                          <svg className="w-3 h-3 opacity-50 flex-shrink-0" viewBox="0 0 6 10" fill="currentColor"><circle cx="1" cy="2" r="1"/><circle cx="5" cy="2" r="1"/><circle cx="1" cy="5" r="1"/><circle cx="5" cy="5" r="1"/><circle cx="1" cy="8" r="1"/><circle cx="5" cy="8" r="1"/></svg>
                          <span className="truncate">{label}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemovePane(paneId)}
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          aria-label="Remove pane"
                          data-testid={`grid-cell-remove-${rowIdx}-${colIdx}`}
                        >
                          ✕
                        </Button>
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                        <LazyPaneRenderer
                          paneId={paneId}
                          selectedVerse={selectedVerse}
                          aiInsights={aiInsights}
                          showAIInsights={showAIInsights}
                          onVerseChange={onVerseChange}
                          onAIAnalyze={onAIAnalyze}
                          isLoadingAI={isLoadingAI}
                        />
                      </div>
                    </div>
                  </div>
                </ResizablePane>
              );
            })}
          </div>

          {/* Row resizer (not after last row) */}
          {rowIdx < layoutGrid.length - 1 && (
            <RowResizer
              isHovered={hoveredResizer === rowIdx}
              onHover={(over) => setHoveredResizer(over ? rowIdx : null)}
              onResize={(e) => startRowResize(e, rowIdx)}
            />
          )}

          {/* Insert-row drop zone after this row */}
          <InsertRowDropZone
            index={rowIdx + 1}
            dragging={!!dragSource}
            active={dragOverNewRowAt === rowIdx + 1 && !!dragSource}
            onDragOver={(e) => handleNewRowDragOver(e, rowIdx + 1)}
            onDrop={(e) => handleNewRowDrop(e, rowIdx + 1)}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export default DashboardGridBuilder;
